import { useCallback, useState } from 'react';
import { useAsyncValue } from 'framework/utils';
import { TextField } from 'framework/ui/field';
import OneClickButton from 'infrastructure/security/authentication/OneClick/OneClickButton';
import RedirectButton from 'infrastructure/security/authentication/OneClick/RedirectButton';
import useOneClickService from 'infrastructure/security/authentication/OneClick/oneClickService';
import OneClickPasswordForm from 'infrastructure/security/authentication/OneClick/OneClickPasswordForm';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import { DataGridLayout, ListPageLayout, SimpleList } from 'framework/list';

const OneClick = () => {
  const oneClickPassword = sessionStorage.getItem('oneClickPassword');
  const [hasPassword, setHasPassword] = useState(oneClickPassword !== 'null');
  const { isAuthenticated } = useAuthenticationService();
  const isUserAuthenticated = useAsyncValue(isAuthenticated);

  const setPassword = (passwordParam: string | null) => {
    sessionStorage.setItem('oneClickPassword', passwordParam!);
    setHasPassword(passwordParam !== null);
  };

  const getPasswordDto = useCallback(() => {
    return {
      password: oneClickPassword,
    };
  }, [oneClickPassword]);

  const { getUsers } = useOneClickService();
  const getUsersWrapper = useCallback(async () => {
    const users = await getUsers(getPasswordDto()).catch(() => {
      setPassword(null);
    });
    return { result: users };
    // Suppressed warnings because async function getUsers is excluded from dependencies because it calls rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPasswordDto]);

  const OneClickList = useCallback(() => {
    return (
      <SimpleList getDataMethod={() => getUsersWrapper()}>
        <ListPageLayout actions={!isUserAuthenticated ? [<RedirectButton key="redirect-button" />] : []}>
          <DataGridLayout
            showTotal={false}
            buttons={[<OneClickButton key="onestop-button" password={getPasswordDto()} />]}
            isSmallTable
            rowKeySource="username"
            sxTableWrapper={{
              '& table.MuiTable-root': {
                '@media (max-width: 1499px)': {
                  minWidth: '700px',
                },
              },
              '& .list-btn-container': {
                textAlign: 'right',
                width: '1%',
                whiteSpace: 'nowrap',
              },
            }}
          >
            <TextField source="role" width={300} />
            <TextField source="username" />
          </DataGridLayout>
        </ListPageLayout>
      </SimpleList>
    );
  }, [getPasswordDto, getUsersWrapper, isUserAuthenticated]);

  if (isUserAuthenticated === null) {
    return null;
  }

  return <>{hasPassword ? <OneClickList /> : <OneClickPasswordForm setPassword={setPassword} />}</>;
};

export default OneClick;
